import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Scan, Check, CheckCircle2, Package, Palette, AlertTriangle,
  User, RotateCcw, X, Layers, PencilLine,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';
import * as validationService from '@/services/validationService';

const LocationPutAway = () => {
  const navigate = useNavigate();
  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);
  const locationInputRef = useRef(null);

  // === Staff session (multi-staff, Table-Clearance style) ===
  const [staffList, setStaffList] = useState([]); // [{staff_id, staff_name}]
  const [sessionStarted, setSessionStarted] = useState(false);
  const [showChangeStaff, setShowChangeStaff] = useState(false);
  const [staffScanValue, setStaffScanValue] = useState('');
  const [isValidatingStaff, setIsValidatingStaff] = useState(false);

  // === Put-away state ===
  const [mode, setMode] = useState('scan_bundle'); // scan_bundle | scan_location
  const [scanValue, setScanValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingItem, setPendingItem] = useState(null); // bundle awaiting a location scan
  const [activeLocation, setActiveLocation] = useState(null); // {location_id, footprint, design_name, design_count, learned_capacity}
  const [totalPutAway, setTotalPutAway] = useState(0);
  const [lastAction, setLastAction] = useState(null);
  const [binFulling, setBinFulling] = useState(false);

  // === Manual location fallback (damaged label) ===
  const [positions, setPositions] = useState([]);
  const [showManualPick, setShowManualPick] = useState(false);
  const [manualLocationId, setManualLocationId] = useState('');

  const primaryStaff = staffList[0] || null;
  const staffIds = staffList.map((s) => s.staff_id);

  const focusBundle = useCallback(() => { setTimeout(() => bundleInputRef.current?.focus(), 80); }, []);
  const focusLocation = useCallback(() => { setTimeout(() => locationInputRef.current?.focus(), 80); }, []);
  const focusStaff = useCallback(() => { setTimeout(() => staffInputRef.current?.focus(), 80); }, []);

  useEffect(() => {
    if (!sessionStarted) return;
    greigeStorageApi.getAllPositions()
      .then((d) => setPositions(d.locations || []))
      .catch(() => {});
  }, [sessionStarted]);

  useEffect(() => {
    if (!sessionStarted || showChangeStaff) focusStaff();
    else if (mode === 'scan_bundle') focusBundle();
    else if (mode === 'scan_location') focusLocation();
  }, [mode, sessionStarted, showChangeStaff, focusBundle, focusLocation, focusStaff]);

  // ============ STAFF ============
  const handleStaffScan = async (e) => {
    e.preventDefault();
    const id = staffScanValue.trim();
    if (!id || isValidatingStaff) return;
    if (staffList.some((s) => s.staff_id === id)) {
      toast.info('Staff already added');
      setStaffScanValue('');
      focusStaff();
      return;
    }
    setIsValidatingStaff(true);
    try {
      const result = await validationService.quickValidateStaff(id);
      if (result.valid) {
        setStaffList((prev) => [...prev, { staff_id: result.staffId || id, staff_name: result.staffName || id }]);
        toast.success(`Added ${result.staffName || id}`);
      } else {
        toast.error(result.message || 'Invalid Staff ID');
      }
    } catch {
      toast.error('Invalid Staff ID');
    } finally {
      setStaffScanValue('');
      setIsValidatingStaff(false);
      focusStaff();
    }
  };

  const handleRemoveStaff = (id) => setStaffList((prev) => prev.filter((s) => s.staff_id !== id));

  const handleStartSession = () => {
    if (staffList.length === 0) { toast.error('Add at least one staff'); return; }
    setSessionStarted(true);
    setShowChangeStaff(false);
  };

  const handleChangeStaff = () => {
    setShowChangeStaff(true);
    setStaffList([]);
    setSessionStarted(false);
    setStaffScanValue('');
  };

  // ============ PLACE HELPERS ============
  const doPutAway = async (bundleBarcode, locationId, opts = {}) =>
    greigeStorageApi.putAwayBundle(primaryStaff.staff_id, bundleBarcode, locationId, {
      staffIds,
      staffIndex: totalPutAway,
      ...opts,
    });

  const placeIntoActive = async (bundleBarcode) => {
    try {
      const resp = await doPutAway(bundleBarcode, activeLocation.location_id);
      setTotalPutAway((n) => n + 1);
      setLastAction({ bundle: bundleBarcode, footprint: activeLocation.footprint, design: activeLocation.design_name });
      const newCount = resp.design_count_in_location;
      const capTxt = resp.learned_capacity ? `${newCount}/${resp.learned_capacity}` : `${newCount}`;
      if (resp.location_full) {
        toast.success(`${activeLocation.footprint} is now FULL (${capTxt}). Scan next bundle for a fresh location.`, { duration: 3500 });
        setActiveLocation(null);
        setMode('scan_bundle');
      } else {
        setActiveLocation((prev) => ({ ...prev, design_count: newCount, learned_capacity: resp.learned_capacity ?? prev.learned_capacity }));
        toast.success(`Placed in ${activeLocation.footprint} (${capTxt})`, { duration: 1400 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to put away');
    } finally {
      focusBundle();
    }
  };

  // ============ BUNDLE SCAN ============
  const handleBundleScan = async (e) => {
    e.preventDefault();
    const bundle = scanValue.trim();
    if (!bundle || isProcessing) return;
    setIsProcessing(true);
    setScanValue('');
    try {
      const suggestion = await greigeStorageApi.suggestPutAwayLocation(bundle);
      const design = suggestion.design_name;

      if (!design) {
        toast.error(suggestion.error || 'Bundle cannot be put away');
        focusBundle();
        return;
      }

      // Same design as the active location → place straight in (continuous scan).
      if (activeLocation && design === activeLocation.design_name) {
        const atCap = activeLocation.learned_capacity && activeLocation.design_count >= activeLocation.learned_capacity;
        if (!atCap) {
          await placeIntoActive(bundle);
          return;
        }
      }

      // Different design while a location is active → don't mix; start fresh.
      if (activeLocation && design !== activeLocation.design_name) {
        toast.info(`New design "${design}" — scan a location for it`, { duration: 2500 });
        setActiveLocation(null);
      }

      setPendingItem({
        bundle_barcode: suggestion.bundle_barcode || bundle,
        design_name: design,
        sort_no: suggestion.sort_no,
        color_name: suggestion.color_name,
        weight_kg: suggestion.bundle_weight_kg,
        suggested_location_id: suggestion.suggested ? suggestion.location_id : null,
        suggested_footprint: suggestion.suggested ? suggestion.footprint : null,
        suggestion_reason: suggestion.reason,
        design_bundles_in_location: suggestion.design_bundles_in_location,
        learned_capacity: suggestion.learned_capacity,
      });
      setShowManualPick(false);
      setManualLocationId('');
      setMode('scan_location');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Scan failed');
      focusBundle();
    } finally {
      setIsProcessing(false);
    }
  };

  // ============ LOCATION SCAN (confirm / override) ============
  const placePendingAtLocation = async (locationCode, viaManualId = false) => {
    setIsProcessing(true);
    try {
      const v = await greigeStorageApi.validateGreigeLocation(locationCode, pendingItem.design_name);
      if (!v.valid) {
        toast.error(v.reason || 'Invalid location');
        return false;
      }
      const isOverride = pendingItem.suggested_location_id && v.location_id !== pendingItem.suggested_location_id;
      const resp = await doPutAway(pendingItem.bundle_barcode, v.location_id, {
        overridden: !!isOverride,
        overrideReason: isOverride ? 'Operator scanned a different location' : null,
      });
      setTotalPutAway((n) => n + 1);
      setLastAction({ bundle: pendingItem.bundle_barcode, footprint: v.footprint, design: pendingItem.design_name });
      const newCount = resp.design_count_in_location;
      const capTxt = resp.learned_capacity ? `${newCount}/${resp.learned_capacity}` : `${newCount}`;
      if (resp.location_full) {
        toast.success(`Placed. ${v.footprint} is now FULL (${capTxt}). Scan next bundle for a fresh location.`, { duration: 3500 });
        setActiveLocation(null);
      } else {
        setActiveLocation({
          location_id: v.location_id,
          footprint: v.footprint,
          design_name: pendingItem.design_name,
          design_count: newCount,
          learned_capacity: resp.learned_capacity,
        });
        toast.success(`Active location set: ${v.footprint}${isOverride ? ' (override)' : ''}`, { duration: 1800 });
      }
      setPendingItem(null);
      setShowManualPick(false);
      setManualLocationId('');
      setMode('scan_bundle');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to put away');
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLocationScan = async (e) => {
    e.preventDefault();
    const code = scanValue.trim();
    if (!code || isProcessing || !pendingItem) return;
    setScanValue('');
    const ok = await placePendingAtLocation(code);
    if (ok) focusBundle(); else focusLocation();
  };

  const handleManualConfirm = async () => {
    if (!manualLocationId) { toast.error('Select a location'); return; }
    const ok = await placePendingAtLocation(manualLocationId, true);
    if (ok) focusBundle();
  };

  const handleSkipPending = () => {
    setPendingItem(null);
    setShowManualPick(false);
    setManualLocationId('');
    setScanValue('');
    setMode('scan_bundle');
    focusBundle();
  };

  // ============ BIN FULL (learn capacity for a NEW design) ============
  const handleBinFull = async () => {
    if (!activeLocation || binFulling) return;
    setBinFulling(true);
    try {
      const result = await greigeStorageApi.markGreigeBinFull(
        activeLocation.location_id, null, activeLocation.design_name
      );
      toast.success(`${activeLocation.footprint} marked FULL — learned capacity ${result.learned_capacity} bundles for ${result.design_name || 'design'}`, { duration: 3500 });
      setActiveLocation(null);
      setMode('scan_bundle');
      focusBundle();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark bin full');
    } finally {
      setBinFulling(false);
    }
  };

  const handleChangeLocation = () => {
    setActiveLocation(null);
    setMode('scan_bundle');
    toast.info('Active location cleared — scan next bundle to pick a new location', { duration: 2500 });
    focusBundle();
  };

  // ============ STAFF SCREEN ============
  if (!sessionStarted || showChangeStaff) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex flex-col">
        <header className="bg-[#1A1F2E] text-[#F9FAFB] px-4 h-14 flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => (showChangeStaff ? setShowChangeStaff(false) : navigate('/sections/greige-storage'))}
            className="text-[#F9FAFB] hover:bg-white/10 p-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="font-semibold text-sm">{showChangeStaff ? 'Change Staff' : 'Location Put Away'}</span>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-16 h-16 rounded-full bg-[#1A1F2E]/10 flex items-center justify-center mb-5">
            <User className="w-8 h-8 text-[#1A1F2E]" />
          </div>
          <h2 className="text-lg font-bold text-[#1A1F2E] mb-1">{showChangeStaff ? 'Switch Staff' : 'Scan Staff ID(s)'}</h2>
          <p className="text-sm text-[#5A5F6E] mb-6">Add one or more staff sharing this put-away</p>
          <form onSubmit={handleStaffScan} className="w-full max-w-xs space-y-3">
            <Input
              ref={staffInputRef}
              value={staffScanValue}
              onChange={(e) => setStaffScanValue(e.target.value)}
              placeholder="Scan staff ID badge..."
              className="text-lg h-12 border-[#E5DFD0] bg-white font-mono text-center"
              autoFocus
              disabled={isValidatingStaff}
              data-testid="staff-scan-input"
            />
            <Button
              type="submit"
              disabled={!staffScanValue.trim() || isValidatingStaff}
              className="w-full h-11 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              data-testid="staff-scan-submit"
            >
              {isValidatingStaff ? 'Validating...' : '+ Add Staff'}
            </Button>
          </form>

          {staffList.length > 0 && (
            <div className="w-full max-w-xs mt-4 space-y-2" data-testid="gs-staff-list">
              <p className="text-xs text-[#5A5F6E] font-medium">{staffList.length} staff added:</p>
              <div className="flex flex-wrap gap-2">
                {staffList.map((s) => (
                  <div key={s.staff_id} className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-full pl-3 pr-1 py-1" data-testid={`gs-staff-chip-${s.staff_id}`}>
                    <span className="text-xs font-medium text-green-800">{s.staff_name}</span>
                    <span className="text-[10px] text-green-600">({s.staff_id})</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveStaff(s.staff_id)}
                      className="w-5 h-5 rounded-full bg-green-100 hover:bg-red-200 flex items-center justify-center text-green-700 hover:text-red-700 text-xs"
                      data-testid={`gs-staff-remove-${s.staff_id}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <Button onClick={handleStartSession} className="w-full h-11 bg-[#1A1F2E] hover:bg-[#2a2f3e] text-white" data-testid="gs-start-session-btn">
                Start Session ({staffList.length})
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============ MAIN UI ============
  return (
    <div className="min-h-screen bg-[#FFFBF5] flex flex-col">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] px-3 pt-4 pb-3 flex flex-col gap-1 flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0 flex-1">
            <Button variant="ghost" onClick={() => navigate('/sections/greige-storage')} className="text-[#F9FAFB] hover:bg-white/10 p-1.5 h-8" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex flex-col leading-tight min-w-0">
              <span className="font-semibold text-sm truncate">Location Put Away</span>
              <button onClick={handleChangeStaff} className="text-[10px] text-[#E8DCC8] hover:text-white text-left flex items-center gap-1 truncate" data-testid="change-staff-btn">
                <User className="w-2.5 h-2.5 flex-shrink-0" />
                <span className="truncate">{staffList.length > 1 ? `${staffList.length} staff` : `${primaryStaff?.staff_name} (${primaryStaff?.staff_id})`}</span>
              </button>
            </div>
          </div>
          {totalPutAway > 0 && (
            <span className="bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold flex items-center gap-1 text-xs flex-shrink-0" data-testid="total-putaway-count">
              <CheckCircle2 className="w-3 h-3" />
              {totalPutAway} Put Away
            </span>
          )}
        </div>
      </header>

      <div className="flex-1 px-4 py-3 overflow-y-auto space-y-3 max-w-3xl w-full mx-auto">

        {/* ACTIVE LOCATION BANNER */}
        {activeLocation && (
          <Card className="border-2 border-emerald-400 bg-emerald-50" data-testid="active-location-banner">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs text-emerald-700 uppercase tracking-wide font-semibold">Active Location</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={handleBinFull} disabled={binFulling} className="text-xs text-amber-700 hover:bg-amber-100 h-7 px-2" data-testid="bin-full-btn">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    {binFulling ? 'Saving...' : 'Bin Full'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleChangeLocation} className="text-xs text-emerald-700 hover:bg-emerald-100 h-7 px-2" data-testid="change-location-btn">
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Change
                  </Button>
                </div>
              </div>
              <p className="text-2xl font-black text-[#1A1F2E] tracking-wider mt-1" data-testid="active-location-footprint">{activeLocation.footprint}</p>
              <p className="text-xs text-emerald-800 mt-1">
                {activeLocation.design_name}
                {' · '}
                <span className="font-semibold">
                  {activeLocation.design_count}
                  {activeLocation.learned_capacity ? ` / ${activeLocation.learned_capacity}` : ''} bundle{activeLocation.design_count === 1 ? '' : 's'}
                </span>
                {activeLocation.learned_capacity ? ' (learned capacity)' : ' — tap Bin Full when the stack is full'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* BUNDLE SCAN */}
        {mode === 'scan_bundle' && (
          <form onSubmit={handleBundleScan}>
            <Card className="border-[#E5DFD0] border-2 border-dashed">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Scan className="w-5 h-5 text-[#1A1F2E]" />
                  <p className="text-sm font-semibold text-[#1A1F2E]">
                    {activeLocation ? 'Scan next bundle (same design fills this location)' : 'Scan bundle from stack'}
                  </p>
                </div>
                <Input
                  ref={bundleInputRef}
                  value={scanValue}
                  onChange={(e) => setScanValue(e.target.value)}
                  placeholder="Scan or type bundle barcode..."
                  className="text-lg h-12 border-[#E5DFD0] bg-white font-mono"
                  autoFocus
                  disabled={isProcessing}
                  data-testid="bundle-barcode-input"
                />
                {isProcessing && <p className="text-xs text-[#5A5F6E] mt-2 animate-pulse">Processing...</p>}
              </CardContent>
            </Card>
          </form>
        )}

        {/* LOCATION SCAN (confirm / override) */}
        {mode === 'scan_location' && pendingItem && (
          <div className="space-y-3" data-testid="location-scan-section">
            {/* Bundle info */}
            <Card className="border-[#E5DFD0]">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-4 h-4 text-[#5A5F6E]" />
                  <p className="text-xs text-[#5A5F6E] uppercase tracking-wide">Scanned Bundle</p>
                </div>
                <p className="text-base font-bold text-[#1A1F2E]" data-testid="pending-bundle-design">{pendingItem.design_name}</p>
                <div className="flex flex-wrap gap-3 text-xs text-[#5A5F6E] mt-0.5">
                  {pendingItem.sort_no && <span>Sort: {pendingItem.sort_no}</span>}
                  {pendingItem.color_name && <span className="flex items-center gap-1"><Palette className="w-3 h-3" />{pendingItem.color_name}</span>}
                  {pendingItem.weight_kg ? <span>{Number(pendingItem.weight_kg).toFixed(3)} KG</span> : null}
                </div>
                <p className="text-xs text-[#B0A898] font-mono mt-1">{pendingItem.bundle_barcode}</p>
              </CardContent>
            </Card>

            {/* Suggested location */}
            {pendingItem.suggested_footprint ? (
              <Card className="border-2 border-blue-300 bg-blue-50">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-blue-600 uppercase tracking-wide font-semibold mb-1">Suggested Location</p>
                  <p className="text-xl font-black text-[#1A1F2E] tracking-wider" data-testid="suggested-footprint">{pendingItem.suggested_footprint}</p>
                  {pendingItem.suggestion_reason && <p className="text-xs text-blue-500 mt-1">{pendingItem.suggestion_reason}</p>}
                  <p className="text-xs text-blue-500 mt-1">Scan the location label to confirm — or scan a different one to override.</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-2 border-amber-300 bg-amber-50">
                <CardContent className="p-3 text-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                  <p className="text-sm text-amber-800 font-medium" data-testid="no-bin-suggestion">No suggestion available. Scan any valid location label.</p>
                </CardContent>
              </Card>
            )}

            {/* Location scan input */}
            <form onSubmit={handleLocationScan}>
              <Card className="border-2 border-amber-300 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-5 h-5 text-amber-700" />
                    <p className="text-sm font-semibold text-amber-800">Scan Location to Confirm</p>
                  </div>
                  <Input
                    ref={locationInputRef}
                    value={scanValue}
                    onChange={(e) => setScanValue(e.target.value)}
                    placeholder="Scan location label (e.g. H10 > 01 > A > 01 Lower)..."
                    className="text-lg h-12 border-amber-300 bg-white font-mono"
                    autoFocus
                    disabled={isProcessing}
                    data-testid="location-scan-input"
                  />
                  {isProcessing && <p className="text-xs text-amber-700 mt-2 animate-pulse">Placing...</p>}
                </CardContent>
              </Card>
            </form>

            {/* Manual fallback + skip */}
            {!showManualPick ? (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowManualPick(true)} className="flex-1 h-10 border-[#E5DFD0] text-[#5A5F6E]" data-testid="manual-pick-toggle">
                  <PencilLine className="w-4 h-4 mr-2" /> Can&apos;t scan? Pick location
                </Button>
                <Button variant="ghost" onClick={handleSkipPending} className="h-10 text-[#5A5F6E]" data-testid="skip-btn">
                  <X className="w-4 h-4 mr-1" /> Skip
                </Button>
              </div>
            ) : (
              <Card className="border border-[#E5DFD0]" data-testid="manual-pick-panel">
                <CardContent className="p-3 space-y-2">
                  <p className="text-sm font-medium text-[#1A1F2E]">Pick a location</p>
                  <Select value={manualLocationId} onValueChange={setManualLocationId}>
                    <SelectTrigger data-testid="manual-location-select"><SelectValue placeholder="Select location..." /></SelectTrigger>
                    <SelectContent>
                      {positions.map((p) => (
                        <SelectItem key={p.location_id} value={p.location_id} data-testid={`manual-location-option-${p.location_id}`}>
                          {p.footprint}{p.status === 'full' ? ' (FULL)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button onClick={handleManualConfirm} disabled={isProcessing || !manualLocationId} className="flex-1 h-10 bg-[#2D5F4F] hover:bg-[#1A1F2E]" data-testid="manual-confirm-btn">
                      <Check className="w-4 h-4 mr-2" /> Put Away Here
                    </Button>
                    <Button variant="ghost" onClick={() => { setShowManualPick(false); setManualLocationId(''); focusLocation(); }} className="h-10 text-[#5A5F6E]" data-testid="manual-cancel-btn">
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* LAST ACTION */}
        {lastAction && mode === 'scan_bundle' && (
          <Card className="border-emerald-200 bg-emerald-50/50" data-testid="last-action">
            <CardContent className="p-2.5 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <div className="text-xs text-emerald-800">
                <span className="font-mono font-medium">{lastAction.bundle}</span>
                <span className="mx-1">→</span>
                <span className="font-bold">{lastAction.footprint}</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LocationPutAway;
